import React, { useState, useEffect, useCallback } from "react";
import { Form, Row, Col, Button, Modal, Card, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import PermissionGate from "../../components/common/PermissionGate";

export default function GRNTab({ purchaseOrderId, onUpdate }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGRN, setEditingGRN] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    received_by: "",
    invoice_number: "",
    invoice_date: "",
    delivery_challan_number: "",
    vehicle_number: "",
    transport_name: "",
    remarks: ""
  });
  const [editForm, setEditForm] = useState({
    received_by: "",
    invoice_number: "",
    invoice_date: "",
    delivery_challan_number: "",
    vehicle_number: "",
    transport_name: "",
    remarks: ""
  });
  const [documents, setDocuments] = useState([{ file: null, label: "" }]);
  const [existingDocuments, setExistingDocuments] = useState([]); // For editing
  const [editNewDocuments, setEditNewDocuments] = useState([{ file: null, label: "" }]); // New docs during edit

  // Helper to add more doc fields
  const addDocField = (isEdit = false) => {
    if (isEdit) {
      setEditNewDocuments([...editNewDocuments, { file: null, label: "" }]);
    } else {
      setDocuments([...documents, { file: null, label: "" }]);
    }
  };

  // Helper to remove doc field
  const removeDocField = (index, isEdit = false) => {
    if (isEdit) {
      setEditNewDocuments(editNewDocuments.filter((_, i) => i !== index));
    } else {
      setDocuments(documents.filter((_, i) => i !== index));
    }
  };

  const handleDocChange = (index, field, value, isEdit = false) => {
    if (isEdit) {
      const updated = [...editNewDocuments];
      updated[index][field] = value;
      setEditNewDocuments(updated);
    } else {
      const updated = [...documents];
      updated[index][field] = value;
      setDocuments(updated);
    }
  };

  // Permissions
  const canEditGRN = useHasPermission(PERMISSIONS.GRN_EDIT);
  const canDeleteGRN = useHasPermission(PERMISSIONS.GRN_DELETE);

  const fetchGRNs = useCallback(async () => {
    try {
      if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
        console.warn('GRNTab: invalid purchaseOrderId, skipping fetchGRNs', purchaseOrderId);
        return;
      }

      const res = await axios.get(`${config.API_BASE_URL}/grn/${purchaseOrderId}`);
      setGrns(res.data);
    } catch (err) {
      console.error("Fetch GRNs error:", err);
      toast.error("Failed to load GRNs");
    }
  },[purchaseOrderId]);

  const fetchQualityEntries = useCallback(async () => {
    try {
      if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
        return;
      }

      const res = await axios.get(`${config.API_BASE_URL}/quality-checks/${purchaseOrderId}`);
      setQualityEntries(res.data);
    } catch (err) {
      console.error("Fetch quality entries error:", err);
    }
  },[purchaseOrderId]);

  useEffect(() => {
    if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
      console.warn('GRNTab: invalid purchaseOrderId in useEffect, skipping fetchGRNs', purchaseOrderId);
      return;
    }

    fetchGRNs();
    fetchQualityEntries();
  }, [purchaseOrderId, fetchGRNs, fetchQualityEntries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        formData.append(key, form[key]);
      });
      formData.append('purchaseOrderId', purchaseOrderId);
      
      const labels = [];
      documents.forEach(doc => {
        if (doc.file) {
          formData.append('documents', doc.file);
          labels.push(doc.label || "Other");
        }
      });
      formData.append('documentLabels', JSON.stringify(labels));

      await axios.post(`${config.API_BASE_URL}/grn`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("GRN created successfully");
      setForm({
        received_by: "",
        invoice_number: "",
        invoice_date: "",
        delivery_challan_number: "",
        vehicle_number: "",
        transport_name: "",
        remarks: ""
      });
      setDocuments([{ file: null, label: "" }]);
      setShowModal(false);
      await fetchGRNs();
      onUpdate();
      
      // Trigger refresh in parent component
      window.dispatchEvent(new CustomEvent('grnUpdated'));
    } catch (err) {
      console.error("Create GRN error:", err);
      toast.error("Failed to create GRN");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Received': return 'warning';
      case 'Quality Checked': return 'info';
      case 'Unloaded': return 'success';
      default: return 'secondary';
    }
  };

  const hasQualityEntries = (grnId) => {
    return qualityEntries.some(entry => entry.grnId === grnId || entry.grnId?._id === grnId);
  };

  const handleEditGRN = (grn) => {
    if (!canEditGRN) {
      toast.error("You don't have permission to edit GRNs");
      return;
    }

    if (hasQualityEntries(grn._id)) {
      toast.error("Cannot edit GRN as quality check entries already exist for this GRN");
      return;
    }

    setEditingGRN(grn);
    setEditForm({
      received_by: grn.received_by || "",
      invoice_number: grn.invoice_number || "",
      invoice_date: grn.invoice_date ? new Date(grn.invoice_date).toISOString().split('T')[0] : "",
      delivery_challan_number: grn.delivery_challan_number || "",
      vehicle_number: grn.vehicle_number || "",
      transport_name: grn.transport_name || "",
      remarks: grn.remarks || ""
    });
    setExistingDocuments(grn.documents || []);
    setEditNewDocuments([{ file: null, label: "" }]);
    setShowEditModal(true);
  };

  const handleDeleteGRN = async (grn) => {
    if (!canDeleteGRN) {
      toast.error("You don't have permission to delete GRNs");
      return;
    }

    if (hasQualityEntries(grn._id)) {
      toast.error("Cannot delete GRN as quality check entries already exist for this GRN");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete GRN ${grn.grnId}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/grn/${grn._id}`);
      toast.success("GRN deleted successfully");
      await fetchGRNs();
      onUpdate();
      window.dispatchEvent(new CustomEvent('grnUpdated'));
    } catch (err) {
      console.error("Delete GRN error:", err);
      toast.error("Failed to delete GRN");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const removeExistingDoc = (index) => {
    setExistingDocuments(existingDocuments.filter((_, i) => i !== index));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.keys(editForm).forEach(key => {
        formData.append(key, editForm[key]);
      });

      formData.append('existingDocuments', JSON.stringify(existingDocuments));

      const labels = [];
      editNewDocuments.forEach(doc => {
        if (doc.file) {
          formData.append('documents', doc.file);
          labels.push(doc.label || "Other");
        }
      });
      formData.append('documentLabels', JSON.stringify(labels));

      await axios.put(`${config.API_BASE_URL}/grn/${editingGRN._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("GRN updated successfully");
      setEditForm({
        received_by: "",
        invoice_number: "",
        invoice_date: "",
        delivery_challan_number: "",
        vehicle_number: "",
        transport_name: "",
        remarks: ""
      });
      setEditNewDocuments([{ file: null, label: "" }]);
      setExistingDocuments([]);
      setShowEditModal(false);
      setEditingGRN(null);
      await fetchGRNs();
      onUpdate();
      window.dispatchEvent(new CustomEvent('grnUpdated'));
    } catch (err) {
      console.error("Update GRN error:", err);
      toast.error("Failed to update GRN");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold">Goods Received Notes (GRN)</h4>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus me-2"></i>Create New GRN
        </Button>
      </div>

      {grns.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted">No GRNs created yet</p>
        </div>
      ) : (
        <Row>
          {grns.map((grn) => (
            <Col md={6} lg={4} key={grn._id} className="mb-3">
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <strong>{grn.grnId}</strong>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={getStatusColor(grn.status)}>{grn.status}</Badge>
                    <div className="btn-group btn-group-sm">
                      <PermissionGate require={PERMISSIONS.GRN_EDIT}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEditGRN(grn)}
                          disabled={hasQualityEntries(grn._id)}
                          title={hasQualityEntries(grn._id) ? "Cannot edit GRN with existing quality checks" : "Edit GRN"}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                      </PermissionGate>
                      <PermissionGate require={PERMISSIONS.GRN_DELETE}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteGRN(grn)}
                          disabled={hasQualityEntries(grn._id)}
                          title={hasQualityEntries(grn._id) ? "Cannot delete GRN with existing quality checks" : "Delete GRN"}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <small className="text-muted">Received By:</small><br />
                    <strong>{grn.received_by}</strong>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Invoice:</small><br />
                    {grn.invoice_number || 'N/A'}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Vehicle:</small><br />
                    {grn.vehicle_number || 'N/A'}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Date:</small><br />
                    {new Date(grn.createdAt).toLocaleDateString('en-IN')}
                  </div>
                  {grn.documents && grn.documents.length > 0 && (
                    <div className="mb-2">
                      <small className="text-muted">Documents:</small><br />
                      <div className="d-flex flex-wrap gap-1">
                        {grn.documents.map((doc, idx) => (
                          <a key={idx} href={config.DOC_BASE_URL + doc.url} target="_blank" rel="noreferrer" className="badge bg-light text-primary border text-decoration-none">
                            <i className="fas fa-file-pdf me-1"></i> {doc.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {hasQualityEntries(grn._id) && (
                    <div className="mt-2">
                      <Badge bg="info" className="me-1">
                        <i className="fas fa-check-circle me-1"></i>Quality Checked
                      </Badge>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New GRN</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={6}>
                <Form.Label>Received By *</Form.Label>
                <Form.Control
                  name="received_by"
                  value={form.received_by}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  name="invoice_number"
                  value={form.invoice_number}
                  onChange={handleChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Invoice Date</Form.Label>
                <Form.Control
                  type="date"
                  name="invoice_date"
                  value={form.invoice_date}
                  onChange={handleChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Delivery Challan No.</Form.Label>
                <Form.Control
                  name="delivery_challan_number"
                  value={form.delivery_challan_number}
                  onChange={handleChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Vehicle Number</Form.Label>
                <Form.Control
                  name="vehicle_number"
                  value={form.vehicle_number}
                  onChange={handleChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Transport Name</Form.Label>
                <Form.Control
                  name="transport_name"
                  value={form.transport_name}
                  onChange={handleChange}
                />
              </Col>
              <Col md={12}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Documents</Form.Label>
                  <Button variant="outline-primary" size="sm" onClick={() => addDocField()}>
                    <i className="fas fa-plus me-1"></i>Add Document
                  </Button>
                </div>
                {documents.map((doc, idx) => (
                  <Row key={idx} className="mb-2 align-items-end">
                    <Col md={5}>
                      <Form.Label className="small">Label (e.g. Invoice)</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Label"
                        value={doc.label}
                        onChange={(e) => handleDocChange(idx, "label", e.target.value)}
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Label className="small">File</Form.Label>
                      <Form.Control
                        size="sm"
                        type="file"
                        onChange={(e) => handleDocChange(idx, "file", e.target.files[0])}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                    </Col>
                    <Col md={2}>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => removeDocField(idx)}
                        disabled={documents.length === 1 && !doc.file}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Form.Text className="text-muted">
                  Upload invoices, challans, or other related documents with labels
                </Form.Text>
              </Col>
              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating GRN...' : 'Create GRN'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit GRN Modal */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setEditingGRN(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit GRN - {editingGRN?.grnId}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={6}>
                <Form.Label>Received By *</Form.Label>
                <Form.Control
                  name="received_by"
                  value={editForm.received_by}
                  onChange={handleEditChange}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  name="invoice_number"
                  value={editForm.invoice_number}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Invoice Date</Form.Label>
                <Form.Control
                  type="date"
                  name="invoice_date"
                  value={editForm.invoice_date}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Delivery Challan No.</Form.Label>
                <Form.Control
                  name="delivery_challan_number"
                  value={editForm.delivery_challan_number}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Vehicle Number</Form.Label>
                <Form.Control
                  name="vehicle_number"
                  value={editForm.vehicle_number}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Transport Name</Form.Label>
                <Form.Control
                  name="transport_name"
                  value={editForm.transport_name}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Existing Documents</Form.Label>
                {existingDocuments.length > 0 ? (
                  <div className="mb-3">
                    {existingDocuments.map((doc, idx) => (
                      <div key={idx} className="d-flex align-items-center justify-content-between border p-2 mb-1 rounded">
                        <span>
                          <Badge bg="info" className="me-2">{doc.label}</Badge>
                          <a href={config.DOC_BASE_URL + doc.url} target="_blank" rel="noreferrer">View File</a>
                        </span>
                        <Button variant="outline-danger" size="sm" onClick={() => removeExistingDoc(idx)}>
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small">No existing documents</p>
                )}

                <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                  <Form.Label className="mb-0">Add New Documents</Form.Label>
                  <Button variant="outline-primary" size="sm" onClick={() => addDocField(true)}>
                    <i className="fas fa-plus me-1"></i>Add More
                  </Button>
                </div>
                {editNewDocuments.map((doc, idx) => (
                  <Row key={idx} className="mb-2 align-items-end">
                    <Col md={5}>
                      <Form.Label className="small">Label</Form.Label>
                      <Form.Control
                        size="sm"
                        placeholder="Label"
                        value={doc.label}
                        onChange={(e) => handleDocChange(idx, "label", e.target.value, true)}
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Label className="small">File</Form.Label>
                      <Form.Control
                        size="sm"
                        type="file"
                        onChange={(e) => handleDocChange(idx, "file", e.target.files[0], true)}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                    </Col>
                    <Col md={2}>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => removeDocField(idx, true)}
                        disabled={editNewDocuments.length === 1 && !doc.file}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Col>
              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="remarks"
                  value={editForm.remarks}
                  onChange={handleEditChange}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowEditModal(false);
              setEditingGRN(null);
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating GRN...' : 'Update GRN'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
